<template>
  <app-collapse :is-open="true">
    <template #heading>
      <template v-if="!isPayable && !isLoading">
        <status-message
          color="error"
          message="Agency details"
        />
      </template>
      <template v-else>
        <span data-sentry-unmask>Agency details</span>
      </template>
    </template>
    <app-card
      variant="muted"
      body-class="space-y-3"
    >
      <data-property
        class="items-start"
        label="Identifier"
        direction="horizontal"
        :is-loading="isLoading"
      >
        <template #value>
          <div class="text-right">
            <p>{{ AgencyIdentifierKindCopy[agencyIdentifier.kind] }} {{ agencyIdentifier.value }}</p>
            <text-truncate
              v-if="shouldShowIdentifierDisplayName"
              :max-length="28"
              class="text-xs text-gray-600"
              :value="identifierDisplayName"
            />
          </div>
        </template>
      </data-property>
      <data-property
        label="Beneficiary"
        direction="horizontal"
        :is-loading="isLoading"
      >
        <template #value>
          <text-truncate
            :value="agencyIdentifier.transfer_party?.business_name || 'N/A'"
            :max-length="28"
          />
        </template>
      </data-property>
      <data-property
        label="Agency"
        direction="horizontal"
        :is-loading="isLoading"
      >
        <template #value>
          <text-truncate
            :value="agencyIdentifier.agency_name || 'N/A'"
            :max-length="28"
          />
        </template>
      </data-property>
      <data-property
        class="items-start"
        direction="horizontal"
        label="Advisor"
        :is-loading="isLoading"
      >
        <template #value>
          <div class="text-right">
            <template v-if="hasAdvisorNameAndEmail">
              <p>{{ advisorName }}</p>
              <p class="text-xs text-gray-600">{{ advisorEmail }}</p>
            </template>
            <template v-else-if="hasAdvisorEmailOnly">
              <p>{{ advisorEmail }}</p>
              <p class="text-xs text-gray-600">(Advisor name not available)</p>
            </template>
            <template v-else>
              <p>{{ advisorName || 'N/A' }}</p>
            </template>
          </div>
        </template>
      </data-property>
      <data-property
        direction="horizontal"
        label="Email"
        :is-loading="isLoading"
      >
        <template #value>
          <text-truncate
            :value="agencyIdentifier.transfer_party?.email || 'N/A'"
            :max-length="32"
          />
        </template>
      </data-property>
      <data-property
        direction="horizontal"
        label="Bank information"
        :is-loading="isLoading"
        :is-invalid="!isPayable && !isLoading"
        data-sentry-unmask
      >
        <template #value>
          <agency-payable-badge
            :status="isPayable ? PayableStatus.PAYABLE : PayableStatus.NOT_PAYABLE"
            non-payable-message="Missing"
          />
        </template>
      </data-property>

      <data-property
        direction="horizontal"
        label="Country"
        :is-loading="isLoading"
        data-sentry-unmask
      >
        <template #value>
          <div class="flex items-center gap-2">
            <country-flag
              v-if="isPayable"
              :country-code="agencyIdentifier.transfer_party?.country_code"
              class="size-5"
            />
            <span>{{ agencyIdentifier.transfer_party?.country_code || 'N/A' }}</span>
          </div>
        </template>
      </data-property>
      <data-property
        direction="horizontal"
        label="Currency"
        :value="agencyIdentifier.transfer_party?.currency_alpha_code || 'N/A'"
        :is-loading="isLoading"
        data-sentry-unmask
      />
    </app-card>
  </app-collapse>
</template>

<script setup>
import { computed } from 'vue';
import { abbreviateName } from '@/utils/string';

import AgencyPayableBadge from '@/components/agencies/AgencyPayableBadge.vue';
import { PayableStatus } from '@/constants/payableStatus.js';
import { AgencyIdentifierKindCopy } from '@/constants/agencies.js';

const props = defineProps({
  reservation: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  isLoading: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const agencyIdentifier = computed(() => props.reservation?.data?.agency_identifier || {});
const isPayable = computed(() => !!agencyIdentifier.value?.transfer_party);

const identifierDisplayName = computed(() => agencyIdentifier.value?.display_name);
const beneficiaryName = computed(() => agencyIdentifier.value?.transfer_party?.business_name);
const agencyName = computed(() => agencyIdentifier.value?.agency_name);

const shouldShowIdentifierDisplayName = computed(
  () =>
    identifierDisplayName.value &&
    identifierDisplayName.value.toLowerCase() !== agencyName.value?.toLowerCase() &&
    identifierDisplayName.value.toLowerCase() !== beneficiaryName.value?.toLowerCase()
);

const advisorName = computed(() =>
  abbreviateName(props.reservation?.data?.advisor_first_name, props.reservation?.data?.advisor_last_name)
);
const advisorEmail = computed(() => props.reservation?.data?.advisor_email);
const hasAdvisorNameAndEmail = computed(() => !!(advisorName.value && advisorEmail.value));
const hasAdvisorEmailOnly = computed(() => !!(!advisorName.value && advisorEmail.value));
</script>
